/**
 * Utilitaire standardisé pour la gestion d'erreurs
 * ISM-28: Améliorations Messages d'Erreur
 *
 * Principe SOLID: Single Responsibility - Gère uniquement la standardisation des erreurs
 */

import { toast } from '@/components/ui/use-toast';
import i18n from '@/lib/i18n/i18n';
import { resolveServiceErrorKey } from '@/lib/i18n/errorCatalog';

/**
 * Contexte d'erreur pour le logging et le debugging
 */
export interface ErrorContext {
  action: string;
  component?: string;
  userId?: string;
  [key: string]: unknown;
}

/**
 * Options pour la gestion d'erreurs
 */
export interface ErrorHandlerOptions {
  showToast?: boolean;
  retry?: () => Promise<unknown>;
  maxRetries?: number;
  customMessage?: string;
  silent?: boolean; // Ne pas logger l'erreur
}

/**
 * Type d'erreur pour le mapping des messages
 */
export type ErrorType =
  | 'network'
  | 'timeout'
  | 'permission'
  | 'validation'
  | 'not_found'
  | 'server'
  | 'unknown';

/**
 * Sévérité de l'erreur pour déterminer l'impact UX
 */
export type ErrorSeverity = 'info' | 'warning' | 'blocking' | 'critical';

/**
 * Détecte la sévérité de l'erreur
 */
export function getErrorSeverity(error: unknown, type?: ErrorType): ErrorSeverity {
  const errorType = type || getErrorType(error);

  switch (errorType) {
    case 'network':
    case 'timeout':
    case 'server':
      return 'blocking'; // Peut empêcher l'affichage des données
    case 'permission':
      return 'blocking'; // Empêche l'action
    case 'not_found':
      return 'blocking'; // Ressource manquante
    case 'validation':
      return 'warning'; // Erreur de formulaire, non critique pour l'app
    default:
      return 'warning';
  }
}

/**
 * Détecte le type d'erreur à partir du message
 */
export function getErrorType(error: unknown): ErrorType {
  if (!(error instanceof Error)) {
    return 'unknown';
  }

  const message = error.message.toLowerCase();

  if (message.includes('network') || message.includes('fetch') || message.includes('failed to fetch')) {
    return 'network';
  }
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'timeout';
  }
  if (message.includes('permission') || message.includes('unauthorized') || message.includes('forbidden')) {
    return 'permission';
  }
  if (message.includes('validation') || message.includes('invalid') || message.includes('doit être')) {
    return 'validation';
  }
  if (message.includes('not found') || message.includes('404')) {
    return 'not_found';
  }
  if (message.includes('server') || message.includes('500') || message.includes('internal')) {
    return 'server';
  }

  return 'unknown';
}

/**
 * Resolves a user-facing error message in the active UI language.
 */
export function getErrorMessage(error: unknown, action: string, customMessage?: string): string {
  if (customMessage) {
    return customMessage;
  }

  if (!(error instanceof Error)) {
    return i18n.t('errors.generic');
  }

  const message = error.message;
  const mappedKey = resolveServiceErrorKey(message);
  if (mappedKey && i18n.exists(mappedKey)) {
    return i18n.t(mappedKey);
  }

  if (message.includes('date de début doit être dans le futur') || message.includes('start date must be in the future')) {
    return i18n.t('errors.validationStartDateFuture', { defaultValue: i18n.t('errors.validation') });
  }
  if (message.includes('date de fin doit être après') || message.includes('end date must be after')) {
    return i18n.t('errors.validationEndDateAfterStart', { defaultValue: i18n.t('errors.validation') });
  }

  const errorType = getErrorType(error);
  switch (errorType) {
    case 'network':
      return i18n.t('errors.network');
    case 'timeout':
      return i18n.t('errors.timeout');
    case 'permission':
      return i18n.t('errors.permission');
    case 'validation':
      return i18n.t('errors.validation');
    case 'not_found':
      return i18n.t('errors.notFound');
    case 'server':
      return i18n.t('errors.server');
    default:
      if (i18n.language?.startsWith('ar') && !/[\u0600-\u06FF]/.test(message)) {
        return i18n.t('errors.generic');
      }
      return message || i18n.t('errors.generic');
  }
}

/**
 * Vérifie si l'erreur est une erreur réseau (peut être retentée)
 */
export function isNetworkError(error: unknown): boolean {
  const errorType = getErrorType(error);
  return errorType === 'network' || errorType === 'timeout';
}

/**
 * Retry avec backoff exponentiel
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Ne pas retry si ce n'est pas une erreur réseau
      if (!isNetworkError(error)) {
        throw error;
      }

      // Dernière tentative, lancer l'erreur
      if (attempt === maxRetries - 1) {
        break;
      }

      // Attendre avant de retry (backoff exponentiel)
      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Gère une erreur de manière standardisée
 * 
 * @param error - L'erreur à gérer
 * @param context - Le contexte de l'erreur (action, component, etc.)
 * @param options - Options de gestion (toast, retry, etc.)
 * 
 * @example
 * ```typescript
 * try {
 *   await someOperation();
 * } catch (error) {
 *   handleError(error, { action: 'createBooking', component: 'BookingForm' }, {
 *     showToast: true,
 *     retry: () => someOperation()
 *   });
 * }
 * ```
 */
export function handleError(
  error: unknown,
  context: ErrorContext,
  options: ErrorHandlerOptions = {}
): void {
  const {
    showToast = true,
    retry,
    maxRetries = 3,
    customMessage,
    silent = false,
  } = options;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const userMessage = getErrorMessage(error, context.action, customMessage);

  // Logger l'erreur avec contexte (sauf si silent)
  if (!silent) {
    if (import.meta.env.DEV) {
      console.error(`[${context.component || 'Unknown'}] ${context.action}:`, {
        error: errorMessage,
        context,
        timestamp: new Date().toISOString(),
        errorType: getErrorType(error),
      });
    }
    // En production, on pourrait logger vers un service externe (Sentry, etc.)
  }

  // Afficher toast si demandé
  if (showToast) {
    toast({
      variant: 'destructive',
      title: i18n.t('common.error'),
      description: userMessage,
    });
  }

  // Retry automatique pour erreurs réseau si une fonction retry est fournie
  if (retry && isNetworkError(error)) {
    retryWithBackoff(retry, maxRetries).catch((retryError) => {
      // Si le retry échoue, afficher un message d'erreur final
      if (showToast) {
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: i18n.t('errors.network'),
        });
      }
      if (import.meta.env.DEV) {
        console.error('Retry failed:', retryError);
      }
    });
  }
}

/**
 * Wrapper pour les fonctions async avec gestion d'erreurs automatique
 * 
 * @example
 * ```typescript
 * const result = await withErrorHandling(
 *   () => createBooking(data),
 *   { action: 'createBooking', component: 'BookingForm' }
 * );
 * ```
 */
export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  context: ErrorContext,
  options?: ErrorHandlerOptions
): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    handleError(error, context, options);
    return null;
  }
}

